import { Controller } from "stimulus";
import { maybeConnectBridge } from "utils/hotwire";
import { getAudioPlayerController } from "utils/audio_player";


/** 🚨🚨🚨🚨 WARNING: This controller has a bridge component counterpart. 🚨🚨🚨🚨
 * 
 * This means that the functionality provided below is handled natively in the mobile apps.
 * Ensure that any changes to this controller are also reflected in the bridge component counterpart, and
 * tested in both Android and iOS.
 * 
 * You can find the bridge component counterpart in:
 * app/components/media/bridge/playback_controls_controller.js
 */

export default class PlaybackControlsController extends Controller {
  static targets = [
    "hideable",
    "progressBar",
    "currentTime",
    "duration",
    "muted",
    "unmuted",
    "airplay",
    "playback0_9",
    "playback1",
    "playback1_1",
    "playback1_5",
    "playback2",
    "rateLabel",
  ]
  static values = {
    itemId: String,
    itemType: String,
    duration: Number
  }

  initialize() {
    this.onTimeUpdate = this.onTimeUpdate.bind(this);
    this.onMuteChange = this.onMuteChange.bind(this);
    this.onRateChange = this.onRateChange.bind(this);
  }

  connect() {
    const bridged = maybeConnectBridge(this.element, "play-button");

    // On web a single label shows the current rate, whatever the player's rate list is. The
    // per-rate elements stay in the markup (hidden here) because the bridge controller owns them.
    if (!bridged && this.hasRateLabelTarget) {
      this.rateLabelTarget.hidden = false;
      this.hidePerRateTargets();
    }

    window.addEventListener("supercast-timeupdate", this.onTimeUpdate);
    window.addEventListener("supercast-mute-change", this.onMuteChange);
    window.addEventListener("supercast-rate-change", this.onRateChange);
  }

  disconnect() {
    window.removeEventListener("supercast-timeupdate", this.onTimeUpdate);
    window.removeEventListener("supercast-mute-change", this.onMuteChange);
    window.removeEventListener("supercast-rate-change", this.onRateChange);
  }

  onTimeUpdate(event) {
    if (event.detail.itemId == this.itemIdValue && event.detail.itemType == this.itemTypeValue) {
      this.show();
      this.updateProgress(event);
    } else {
      this.hide();
    }
  }

  togglePlaybackRate() {
    this.getMiniPlayer().togglePlaybackRate();
  }

  jumpBack() {
    this.getMiniPlayer().jumpBack();
  }

  jumpForward() {
    this.getMiniPlayer().jumpForward();
  }

  toggleMute() {
    this.getMiniPlayer().toggleMute();
  }

  showAirplay() {
    this.getMiniPlayer().showAirplay();
  }

  onMuteChange(event) {
    this.mutedTarget.hidden = !event.detail.muted;
    this.unmutedTarget.hidden = event.detail.muted;
  }

  onRateChange(event) {
    if (!this.hasRateLabelTarget) return;
    this.rateLabelTarget.textContent = `${event.detail.playbackRate}×`;
  }

  hidePerRateTargets() {
    [
      this.playback0_9Target,
      this.playback1Target,
      this.playback1_1Target,
      this.playback1_5Target,
      this.playback2Target,
    ].forEach(target => { target.hidden = true; });
  }

  seek(event) {
    this.getMiniPlayer().seek((event.currentTarget.value / 100) * this.durationValue);
    this.progressBarTarget.style.setProperty("--seek-pct", `${event.currentTarget.value}%`);
  }

  show() {
    this.hideableTargets.forEach(target => target.classList.remove("opacity-0"));
  }

  hide() {
    this.hideableTargets.forEach(target => target.classList.add("opacity-0"));
  }

  updateProgress(event) {
    if (!this.hasProgressBarTarget) {
      return;
    }

    const rawPct = (event.detail.currentTime / event.detail.duration) * 100;
    const pct = Number.isFinite(rawPct) ? rawPct : 0;
    this.progressBarTarget.value = pct;
    this.progressBarTarget.style.setProperty("--seek-pct", `${pct}%`);
    this.currentTimeTarget.textContent = this.formatTime(event.detail.currentTime);
    this.durationTarget.textContent = this.formatTime(event.detail.duration);
  }

  getMiniPlayer() {
    return getAudioPlayerController(this.application);
  }

  formatTime(time) {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
}